import { Button, Col, Heading, Row, Stack, TabList } from '@workramp/ui';
import { useEffect, useMemo } from 'react';
import { ErrorPage, StatPanel } from '~/features/common';
import { I18n } from '~/common/explicit-exports';
import { hideLoader, showLoader } from '~/common';
import { useGetAssetsStats, useGetDashboardVerification } from '../../api';
import { CMSDashboardActivityTable } from '../CMSDashbboardActivityTable';
import { CMSDashboardVerificationTable } from '../CMSDashboardVerificationTable';
import {
  AssetDashboardStack,
  StyledContainer,
  WrapperExportButton,
  WrapperHeaderDashboard,
  WrapperStatsPanelDashboard,
} from './CMSDashboard.styles';

const TRANSLATED_METRICS_VIEWS = I18n.t('js.admin.cms.metrics.views');
const TRANSLATED_METRICS_DOWNLOADS = I18n.t('js.admin.cms.metrics.downloads');
const TRANSLATED_METRICS_COPIES = I18n.t('js.admin.cms.metrics.copies');
const TRANSLATED_METRICS_SHARES = I18n.t('js.admin.cms.metrics.shares');
const TRANSLATED_DASHBOARD = I18n.t('js.admin.cms.dashboard');
const EXPORT_CSV = I18n.t('js.admin.cms.export_csv');
const PERFORMANCE = I18n.t('js.admin.cms.performance');
const VERIFICATION = I18n.t('js.admin.cms.verification');

const EMPTY_STATS_STRING = '--';

export const CMSDashboard = () => {
  const getAssetsStatsQuery = useGetAssetsStats();
  const getVerification = useGetDashboardVerification();

  const downloadExportResults = () => {
    window.open(`/api/cms/export_assets`);
  };

  useEffect(() => {
    if (getAssetsStatsQuery.isLoading || getVerification.isLoading) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [getAssetsStatsQuery.isLoading, getVerification.isLoading]);

  const error = useMemo(
    () => getAssetsStatsQuery.error || getVerification.error,
    [getAssetsStatsQuery.error, getVerification.error],
  );

  if (error) {
    return <ErrorPage status={error.status} />;
  }

  return (
    <AssetDashboardStack size={6}>
      {/* StyledContainer is necessary to eliminate the default padding of the container.
       This removal is important to allow the table within the container to
       stretch from one end to end. */}
      <StyledContainer isFluid>
        <WrapperHeaderDashboard>
          <Row align="center" justify="space-between">
            <Col>
              <Heading as="h2" level="md">
                {TRANSLATED_DASHBOARD}
              </Heading>
            </Col>
          </Row>
        </WrapperHeaderDashboard>
        <TabList verticalSpacing={2} padding={8} paddingTabPanel={0} aria-label="CMS Dashboard Tablist">
          <TabList.Item key={PERFORMANCE} title={PERFORMANCE}>
            <Stack size={6}>
              <WrapperExportButton>
                <Row align="center" justify="flex-end">
                  <Col xs="auto">
                    <Button
                      size="lg"
                      color="secondary"
                      variant="outline"
                      onPress={() => {
                        downloadExportResults();
                      }}
                    >
                      {EXPORT_CSV}
                    </Button>
                  </Col>
                </Row>
              </WrapperExportButton>
              <WrapperStatsPanelDashboard>
                <StatPanel>
                  <StatPanel.Item
                    label={TRANSLATED_METRICS_VIEWS}
                    value={
                      getAssetsStatsQuery.data?.totalViews === 0
                        ? EMPTY_STATS_STRING
                        : getAssetsStatsQuery.data?.totalViews
                    }
                  />
                  <StatPanel.Item
                    label={TRANSLATED_METRICS_DOWNLOADS}
                    value={
                      getAssetsStatsQuery.data?.totalDownloads === 0
                        ? EMPTY_STATS_STRING
                        : getAssetsStatsQuery.data?.totalDownloads
                    }
                  />
                  <StatPanel.Item
                    label={TRANSLATED_METRICS_COPIES}
                    value={
                      getAssetsStatsQuery.data?.totalCopies === 0
                        ? EMPTY_STATS_STRING
                        : getAssetsStatsQuery.data?.totalCopies
                    }
                  />
                  <StatPanel.Item
                    label={TRANSLATED_METRICS_SHARES}
                    value={
                      getAssetsStatsQuery.data?.totalShares === 0
                        ? EMPTY_STATS_STRING
                        : getAssetsStatsQuery.data?.totalShares
                    }
                  />
                </StatPanel>
              </WrapperStatsPanelDashboard>
              <CMSDashboardActivityTable />
            </Stack>
          </TabList.Item>
          <TabList.Item
            key={VERIFICATION}
            title={VERIFICATION}
            count={getVerification.data?.length > 0 ? getVerification.data?.length : undefined}
          >
            <CMSDashboardVerificationTable />
          </TabList.Item>
        </TabList>
      </StyledContainer>
    </AssetDashboardStack>
  );
};
